import { ApiClient, type ApiClientError } from '@acies/platform';
import { Cache, Effect, Exit, Schema } from 'effect';
import { HttpClientResponse } from 'effect/http';
import { SearchResult } from '../domain/search-results';
import { runCall } from './runtime';
import { Collection } from './shared';

/**
 * How many searches to keep.
 *
 * A cap, because Effect caches take one. The searches a person makes in a sitting are counted in tens, across the handful of collections a configuration is picked from, so this is far past what anyone will reach — but it is a number rather than no number, and the oldest entries go once it is met.
 */
const searchCapacity = 512;

const decodeSearchResults = HttpClientResponse.schemaBodyJson(Collection(SearchResult));

/** What a search asks for. */
export interface SearchOptions {
	/** The most results to answer with. */
	readonly limit: number;
	/** The text the user typed, matched against what the collection holds. */
	readonly name: string;
}

/** Everything searching a collection can fail with. */
export type SearchError = ApiClientError | Schema.SchemaError;

/**
 * The search as one address: the collection it was made in, and what was asked of it.
 *
 * The collection path carries the store, and so the engine, so a cached answer cannot outlive the account it belongs to — and switching engine cannot show one store the contents of another. It also keeps two collections apart without anything else having to say which one a search was against.
 */
export function searchUrl(path: string, { limit, name }: SearchOptions): string {
	return `${path}?${new URLSearchParams({ limit: String(limit), name }).toString()}`;
}

/** Makes one search, at an address that already says everything about it. */
export function searchAt(client: typeof ApiClient.Service, url: string): Effect.Effect<ReadonlyArray<SearchResult>, SearchError> {
	return Effect.gen(function* () {
		const response = yield* client.get(url);
		const { items } = yield* decodeSearchResults(response);
		return items;
	});
}

/**
 * The searches the API has answered, kept for as long as the page lives.
 *
 * Typing a key walks through the searches for every prefix of it, and a backspace walks back through searches already made: `A`, `AB`, `AB1`, and then `AB` again. A collection does not change while someone is typing into it, so the second `AB` is answered from here.
 *
 * An Effect cache rather than a map of promises, for two things it does that a map does not. Searches made while the same one is still out wait for that one instead of making a second — and anyone types faster than an API answers, so a correction is typed at exactly the moment the first search is in the air. And an
 * answer is kept for ever while a failure is not kept at all, said once as a time to live rather than as a rule to remember to apply.
 *
 * One cache for every collection rather than one each, because it is keyed by the address, which carries the collection as well as the store: see {@link searchUrl}. Two collections cannot answer each other's searches.
 */
const searches = Effect.runSync(
	Cache.makeWith(
		(url: string) =>
			Effect.gen(function* () {
				const client = yield* ApiClient;
				return yield* searchAt(client, url);
			}),
		{
			capacity: searchCapacity,
			// an answer is kept for as long as the page lives; a failure is not kept at all, so the
			// next search for the same thing asks the API rather than being told no again
			timeToLive: (exit) => (Exit.isSuccess(exit) ? 'Infinity' : 0),
			// the client is reached for when something is looked up rather than when this is built,
			// which is what lets the cache be built here at all: asking for it up front would mean a
			// layer, and a layer would mean the runtime importing this module while it imports the
			// runtime back
			requireServicesAt: 'lookup',
		},
	),
);

/**
 * Searches the collection at `path`, answering from {@link searches} where it can.
 *
 * The Effect-native form, for callers that already have a path and are inside a call — which is how the construction model search reaches the cache after resolving its own store path.
 */
export function searchCached(path: string, options: SearchOptions): Effect.Effect<ReadonlyArray<SearchResult>, SearchError, ApiClient> {
	return Cache.get(searches, searchUrl(path, options));
}

/**
 * Lists what the collection at `url` holds, answering from {@link searches} where it can.
 *
 * For a collection short enough to offer in full rather than search — the handful of configurations one inventory item can be had in. The address says everything about the list, a filter on it included, and a list is made and kept the way a search is: under its address, which no search shares, since every search asks
 * for a limit and a name.
 *
 * In full means as much as the API answers with when it is not given a limit, which is a hundred. A list that outgrows that is one to search rather than list.
 */
export function listCached(url: string): Effect.Effect<ReadonlyArray<SearchResult>, SearchError, ApiClient> {
	return Cache.get(searches, url);
}

/**
 * Forgets every search that has been made.
 *
 * The collections are fixed for as long as anyone is typing into them, so nothing in the Configurator calls this yet. Tests do, because a cache that outlives what put things in it would answer one test with what another one arranged.
 */
export const forgetSearches = (): Promise<void> => runCall(() => Cache.invalidateAll(searches));

/**
 * Searches a collection from outside Effect, by the path the engine named it with.
 *
 * For the pickers, which are web components rather than Effect programs. A failure comes back as itself — see {@link runCall} — so a picker can say that the search went wrong rather than that nothing matched. An empty array still means what it says: the API looked, and nothing matched what the user typed — and that
 * answer is remembered like any other.
 *
 * The path is used exactly as it was given and never rebuilt. A lookup parameter carries the path of the collection its value is picked from, written by the engine with a prefix and a store spelling of its own, so taking it apart to put it back together is how you arrive at an address that does not resolve.
 */
export const search = (path: string, limit: number, name: string): Promise<ReadonlyArray<SearchResult> | SearchError> => runCall(() => searchCached(path, { limit, name }));
